// Consultant marketplace (C6.2) — discover approved consultants, propose an engagement, and sign
// it (dual sign-off → active) over the tenant-worker /consultants + /consultant-engagements routes.
// Stripe Connect payouts are L4-Connect (owner-gated) and not surfaced here. PLAN-C6.2.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { humanizeEnum } from "../lib/enum-labels";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select as UiSelect, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ENGAGEMENT_KINDS = ["dpo_as_a_service", "implementation", "external_counsel_retainer", "isms_implementation", "nis2_readiness", "ai_act_conformity_advisory", "dpia_specialist", "audit_preparation", "trust_page_stewardship", "external_counsel_review_per_review"] as const;
const BILLING_MODELS = ["monthly_retainer", "fixed_price", "time_and_materials", "per_deliverable"] as const;

export function ConsultantsRoute() {
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState("");
  const [kind, setKind] = useState<string>("dpo_as_a_service");
  const [billing, setBilling] = useState<string>("monthly_retainer");
  const [startDate, setStartDate] = useState("2026-07-01");

  const consultants = useQuery({ queryKey: ["consultants", "approved"], queryFn: () => api.listApprovedConsultants() as Promise<Array<Record<string, unknown>>> });
  const engagements = useQuery({ queryKey: ["consultant-engagements"], queryFn: () => api.listConsultantEngagements() as Promise<Array<Record<string, unknown>>> });

  const create = useMutation({
    mutationFn: () => api.createConsultantEngagement({ consultantOrgId: orgId, engagementKind: kind, billingModel: billing, startDate }),
    onSuccess: () => { setOrgId(""); qc.invalidateQueries({ queryKey: ["consultant-engagements"] }); },
  });
  const sign = useMutation({
    mutationFn: (engagementId: string) => api.signConsultantEngagement(engagementId, "tenant"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consultant-engagements"] }),
  });

  const orgs = consultants.data ?? [];
  const engs = engagements.data ?? [];

  return (
    <section>
      <PageHeader
        title="Consultants"
        description="Discover certified consultants and engage them — role-scoped, dual-signed, billed through the platform."
      />

      <h2 className="mb-1.5 text-base font-semibold">Directory {consultants.isFetching && <span className="text-xs text-muted-foreground">updating…</span>}</h2>
      {consultants.error && <p className="text-sm text-destructive">Failed: {(consultants.error as Error).message}</p>}
      {orgs.length === 0 && !consultants.isLoading && <p className="text-sm text-muted-foreground">No approved consultants yet.</p>}
      {orgs.length > 0 && (
        <div className="mb-5 rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>{["name", "slug", "sectors", ""].map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {orgs.map((o, i) => {
                const id = o.id as string;
                return (
                  <TableRow key={id ?? i}>
                    <TableCell className="font-medium">{String(o.displayName ?? "—")}</TableCell>
                    <TableCell>{String(o.slug ?? "—")}</TableCell>
                    <TableCell>{Array.isArray(o.sectorsSpecialised) ? (o.sectorsSpecialised as string[]).join(", ") : "—"}</TableCell>
                    <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => setOrgId(id)}>Select</Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <h2 className="mb-1.5 text-base font-semibold">Propose an engagement</h2>
      <form onSubmit={(e) => { e.preventDefault(); if (orgId.trim()) create.mutate(); }} className="my-2 mb-5 flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <Input value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="consultant org id (or Select above)" className="flex-[1_1_280px]" />
        <KindSelect label="kind" value={kind} onChange={setKind} options={ENGAGEMENT_KINDS} />
        <KindSelect label="billing" value={billing} onChange={setBilling} options={BILLING_MODELS} />
        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-auto" />
        <Button type="submit" disabled={!orgId.trim() || create.isPending}>{create.isPending ? "Proposing…" : "Propose"}</Button>
      </form>
      {create.isError && <p className="text-sm text-destructive">{create.error instanceof ApiError ? `Server ${create.error.status}: ${JSON.stringify(create.error.body)}` : (create.error as Error).message}</p>}

      <h2 className="mb-1.5 text-base font-semibold">My engagements {engagements.isFetching && <span className="text-xs text-muted-foreground">updating…</span>}</h2>
      {engs.length === 0 && !engagements.isLoading && <p className="text-sm text-muted-foreground">No engagements yet.</p>}
      {engs.length > 0 && (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>{["org", "kind", "billing", "status", ""].map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {engs.map((g, i) => {
                const id = g.id as string;
                const status = String(g.status ?? "—");
                return (
                  <TableRow key={id ?? i}>
                    <TableCell>{String(g.consultantOrgId ?? "—").slice(0, 8)}…</TableCell>
                    <TableCell>{humanizeEnum(g.engagementKind)}</TableCell>
                    <TableCell>{humanizeEnum(g.billingModel)}</TableCell>
                    <TableCell><Badge variant={status === "active" ? "default" : "secondary"}>{humanizeEnum(status)}</Badge></TableCell>
                    <TableCell className="text-right">{status !== "active" && <Button variant="outline" size="sm" onClick={() => sign.mutate(id)} disabled={sign.isPending}>Sign</Button>}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {sign.isError && <p className="text-sm text-destructive">{(sign.error as Error).message}</p>}
    </section>
  );
}

function KindSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: readonly string[] }) {
  const id = `cs-${label}`;
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={id} className="text-muted-foreground">{label}</Label>
      <UiSelect value={value} onValueChange={onChange}>
        <SelectTrigger id={id} size="sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </UiSelect>
    </div>
  );
}
